import { normalize } from './dates';
import type { Organization, OrgStatus } from './types';

/**
 * Organizações (igrejas) que usam o Escala. Tudo o que é de negócio — membros,
 * ministérios, eventos, escalas, cadastros — pertence a uma, e só quem está nela vê.
 * O Administrador da plataforma cria e gerencia as organizações; o dia a dia fica com o
 * Administrador de cada uma.
 */

/** Domínio exibido no endereço de acesso (`livegospel.app/{slug}`). */
export const APP_HOST = 'livegospel.app';

/** As cores oferecidas ao criar uma organização (a marca no menu lateral e no login). */
export const ORG_COLORS = [
  '#2563EB',
  '#0EA5E9',
  '#0891B2',
  '#0D9488',
  '#059669',
  '#16A34A',
  '#65A30D',
  '#CA8A04',
  '#D97706',
  '#EA580C',
  '#DC2626',
  '#BE123C',
  '#DB2777',
  '#C026D3',
  '#7C3AED',
  '#4F46E5',
  '#475569',
  '#78716C',
] as const;

export const ORG_STATUS_LABEL: Record<OrgStatus, string> = {
  active: 'Ativa',
  suspended: 'Suspensa',
};

/** Endereço de acesso: letras minúsculas sem acento, números e hífen; de 3 a 40. */
export const SLUG = /^[a-z0-9](?:[a-z0-9-]{1,38})[a-z0-9]$/;

export const SLUG_MAX = 40;

/** Sugestão de endereço a partir do nome: "Igreja Comunidade Esperança" → "igreja-comunidade-esperanca". */
export function slugify(name: string): string {
  return normalize(name)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, '');
}

/**
 * Erro do endereço de acesso: formato (`[a-z0-9-]`, de 3 a 40, sem hífen nas pontas) e
 * livre entre as outras organizações. `null` quando está válido.
 */
export function slugError(
  slug: string,
  orgs: readonly Pick<Organization, 'id' | 'slug'>[] = [],
  orgId: string | null = null
): string | null {
  if (!slug) return 'Informe o endereço de acesso.';
  if (slug.length < 3) return 'Use pelo menos 3 caracteres.';
  if (slug.length > SLUG_MAX) return `Use no máximo ${SLUG_MAX} caracteres.`;
  if (!SLUG.test(slug) || slug.includes('--')) {
    return 'Use só letras minúsculas sem acento, números e hífen (sem hífen no começo ou no fim).';
  }
  if (orgs.some((o) => o.id !== orgId && o.slug === slug)) return 'Este endereço já está em uso.';

  return null;
}

/** Erro do nome da organização: obrigatório e com até 80 caracteres. */
export function orgNameError(name: string): string | null {
  const typed = name.trim();

  if (!typed) return 'Informe o nome da organização.';
  if (typed.length > 80) return 'Use no máximo 80 caracteres.';

  return null;
}

/** Endereço de acesso completo: "livegospel.app/comunidade-esperanca". */
export function orgUrl(slug: string): string {
  return `${APP_HOST}/${slug}`;
}

/** Cidade e UF: "São Paulo/SP" (`''` sem nenhum dos dois). */
export function orgPlace(org: Pick<Organization, 'city' | 'uf'>): string {
  return [org.city, org.uf].filter(Boolean).join('/');
}

/** Iniciais para a marca: "Igreja Comunidade Esperança" → "CE" (sem "Igreja", "de"…). */
export function orgInitials(name: string): string {
  const skip = new Set(['igreja', 'de', 'da', 'do', 'das', 'dos', 'e', 'em']);
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && !skip.has(normalize(w)));
  const picked = words.length > 0 ? words : name.trim().split(/\s+/);

  return picked
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/** Busca por nome, endereço de acesso ou cidade, sem acento nem maiúscula. */
export function matchesOrg(org: Pick<Organization, 'name' | 'slug' | 'city'>, query: string): boolean {
  const q = normalize(query.trim()).replace(new RegExp(`^(https?://)?${APP_HOST.replace('.', '\\.')}/`), '');

  if (!q) return true;

  return [org.name, org.slug, org.city].some((field) => normalize(field).includes(q));
}
